// src/utils/proteinOrdering.js
// Ordering rules for the protein substitution list (per Claire, Jul 2026):
//   • Flavor groups run: flavored first, then Grilled, then Unseasoned.
//   • Within a group, proteins are grouped by type: meat, fish, vegan, eggs.

const GRILLED_RE = /grill/i;
const UNSEASONED_RE = /unseason|plain|no season|natural|none/i;

/** Sort rank for a flavor-group heading: flavored (0) → grilled (1) → unseasoned (2). */
export function flavorGroupRank(flavor) {
  const f = String(flavor || '');
  if (UNSEASONED_RE.test(f)) return 2;
  if (GRILLED_RE.test(f)) return 1;
  return 0;
}

// Keyword sets for classifying a protein by type. Order inside each array does
// not matter; the array's index in TYPE_MATCHERS sets the display order.
const TYPE_MATCHERS = [
  // meat
  /\b(beef|steak|sirloin|filet|brisket|lamb|pork|bacon|ham|sausage|chicken|turkey|duck|veal|meatball|bison)\b/i,
  // fish + seafood
  /\b(salmon|cod|tuna|halibut|bass|trout|tilapia|snapper|mahi|sardine|anchovy|shrimp|prawn|crab|lobster|scallop|squid|calamari|mussel|clam|oyster|fish|seafood)\b/i,
  // vegan / plant
  /\b(tofu|tempeh|seitan|chickpea|lentil|bean|edamame|quinoa|plant|vegan|falafel|hummus|nut|almond|walnut|cashew|pea protein|mushroom)\b/i,
  // eggs + dairy proteins
  /\b(egg|eggs|omelet|omelette|frittata|yogurt|greek yogurt|cottage cheese|cheese|paneer|ricotta)\b/i,
];

/** 0 = meat, 1 = fish, 2 = vegan, 3 = eggs/dairy, 4 = unclassified (sorts last). */
export function proteinTypeRank(name) {
  const n = String(name || '');
  for (let i = 0; i < TYPE_MATCHERS.length; i += 1) {
    if (TYPE_MATCHERS[i].test(n)) return i;
  }
  return TYPE_MATCHERS.length;
}

const optionLabel = (o) => o?.variantName || o?.name || '';

/** Sort protein options within a group by type (meat → fish → vegan → eggs), then name. */
export function sortProteinOptions(options = []) {
  return [...options].sort((a, b) => {
    const rank = proteinTypeRank(optionLabel(a)) - proteinTypeRank(optionLabel(b));
    if (rank !== 0) return rank;
    return optionLabel(a).localeCompare(optionLabel(b));
  });
}

/** Sort [flavor, options] pairs into Claire's order, sorting each group's options too. */
export function sortFlavorGroups(groups = []) {
  return [...groups]
    .sort((a, b) => flavorGroupRank(a[0]) - flavorGroupRank(b[0]) || a[0].localeCompare(b[0]))
    .map(([flavor, options]) => [flavor, sortProteinOptions(options)]);
}
